import { useEffect, useMemo, useState, type MouseEvent } from 'react';
import { Link } from 'react-router-dom';
import type { LeadBulkPatch, LeadCollectionRecord, LeadStatus } from '@shared/types';
import { subscribeToShellDeleteRequests } from '@renderer/app/events/shell-delete-events';
import { subscribeToShellDomainInvalidations } from '@renderer/app/events/shell-domain-events';
import { InlineQuickAddPanel, createQuickAddContext } from '@renderer/app/quick-add/quick-add-shared';
import { useShellStore } from '@renderer/app/state/shell-store';
import type { LeadsViewMode } from '@shared/types';
import { BulkEditorRail } from '@renderer/shared/components/bulk-editor-rail';
import {
  CollectionColumnSettingsModal,
  resolveVisibleCollectionColumns,
  type CollectionColumnConfig
} from '@renderer/shared/components/collection-column-settings';
import {
  CardGridSurface,
  CollectionSelectionToggle,
  CollectionToolbar,
  CollectionViewToggle,
  DataTableSurface,
  EmptyCollectionState
} from '@renderer/shared/components/collection-scaffold';
import { CollectionTable, type CollectionSortState } from '@renderer/shared/components/collection-table';
import { Icon } from '@renderer/shared/components/icons';
import { SplitWorkspaceLayout } from '@renderer/shared/components/split-workspace';
import {
  IconButton,
  OverflowMenuButton,
  PanelCard,
  SectionHeader,
  SearchableSelectField,
  SelectField,
  StatusBadge,
  TextField,
  ToolbarGroup
} from '@renderer/shared/components/primitives';
import { useI18n } from '@renderer/shared/i18n';
import { openExternal, toTelUrl } from '@renderer/shared/utils/open-external';
import { LeadImportExportPanel } from './lead-import-export-panel';
import { LeadStatusControl, leadStatusOrder } from './lead-status-control';
import { applyBulkLeadPatch, deleteLeads, listLeads, planLeadContacts, updateLead } from './leads-repository';
import {
  selectLeadCategoryOptions,
  selectLeadCityOptions,
  selectLeadPlannerWeek,
  selectLeadRows,
  selectLeadSelectionItems,
  selectLeadsSummary,
  type LeadPlannerDayViewModel,
  type LeadPlannerItemViewModel,
  type LeadRowViewModel
} from './leads-selectors';
import { useLeadsViewStore, type LeadsCityFilter, type LeadsDueFilter, type LeadsSort } from './leads-view-store';

const leadStatusFilters: Array<'all' | LeadStatus> = [
  'all',
  ...leadStatusOrder
];
type PatchMode = 'unchanged' | 'clear' | 'set';
type SortDirection = 'asc' | 'desc';
type LeadBulkDraft = Omit<LeadBulkPatch, 'leadIds'>;
type LeadPlanningDraft = {
  cityFilter: LeadsCityFilter;
  categoryFilter: 'all' | string;
  statusFilter: LeadStatus[];
  startDate: string;
  dayCount: string;
  leadsPerDay: string;
};

function toTags(value: string): string[] {
  return value
    .split(',')
    .map((entry) => entry.trim())
    .filter(Boolean);
}

function createInitialLeadBulkDraft(): LeadBulkDraft {
  return {
    status: { mode: 'unchanged' },
    customerCategoryId: { mode: 'unchanged' },
    nextContactAt: { mode: 'unchanged' },
    city: { mode: 'unchanged' },
    source: { mode: 'unchanged' },
    lostReason: { mode: 'unchanged' },
    tags: { mode: 'unchanged' }
  };
}

function formatDateInputValue(value: string | null): string {
  return value ? value.slice(0, 10) : '';
}

function toIsoDate(value: string): string {
  return `${value}T12:00:00.000Z`;
}

function toDateKey(date: Date): string {
  return date.toISOString().slice(0, 10);
}

function getWeekStartDate(dateKey: string): string {
  const date = new Date(toIsoDate(dateKey));
  const day = date.getUTCDay();
  const daysFromMonday = day === 0 ? 6 : day - 1;
  date.setUTCDate(date.getUTCDate() - daysFromMonday);
  return toDateKey(date);
}

function addDays(dateKey: string, days: number): string {
  const date = new Date(toIsoDate(dateKey));
  date.setUTCDate(date.getUTCDate() + days);
  return toDateKey(date);
}

function createInitialPlanningDraft(): LeadPlanningDraft {
  return {
    cityFilter: 'all',
    categoryFilter: 'all',
    statusFilter: ['new', 'to_call', 'follow_up'],
    startDate: getWeekStartDate(addDays(toDateKey(new Date()), 7)),
    dayCount: '5',
    leadsPerDay: '12'
  };
}

function leadSortToColumnId(sort: LeadsSort): string {
  switch (sort) {
    case 'recent':
      return 'addedAt';
    case 'contact':
      return 'contact';
    case 'category':
      return 'category';
    case 'status':
      return 'status';
    case 'website':
      return 'website';
    case 'city':
      return 'city';
    case 'name':
      return 'name';
    default:
      return 'nextContact';
  }
}

function leadColumnIdToSort(columnId: string): LeadsSort | null {
  switch (columnId) {
    case 'addedAt':
      return 'recent';
    case 'contact':
      return 'contact';
    case 'category':
      return 'category';
    case 'status':
      return 'status';
    case 'website':
      return 'website';
    case 'city':
      return 'city';
    case 'name':
      return 'name';
    case 'nextContact':
      return 'nextContact';
    default:
      return null;
  }
}

function getDefaultLeadSortDirection(sort: LeadsSort): SortDirection {
  return sort === 'recent' ? 'desc' : 'asc';
}

export function LeadsScreen(): JSX.Element {
  const { locale, t } = useI18n();
  const preferences = useShellStore((state) => state.preferences);
  const savePreferences = useShellStore((state) => state.savePreferences);
  const [collection, setCollection] = useState<LeadCollectionRecord | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [mutationError, setMutationError] = useState<string | null>(null);
  const [updatingStatusLeadId, setUpdatingStatusLeadId] = useState<string | null>(null);
  const [reloadToken, setReloadToken] = useState(0);
  const [leadBulkDraft, setLeadBulkDraft] = useState<LeadBulkDraft>(createInitialLeadBulkDraft);
  const [planningDraft, setPlanningDraft] = useState<LeadPlanningDraft>(createInitialPlanningDraft);
  const [planningMessage, setPlanningMessage] = useState<string | null>(null);
  const [planningBusy, setPlanningBusy] = useState(false);
  const [columnSettingsOpen, setColumnSettingsOpen] = useState(false);
  const {
    clearSelection,
    categoryFilter,
    cityFilter,
    dueFilter,
    plannerWeekStart,
    removeSelectedId,
    selectedPlannerDate,
    selectionSession,
    setCategoryFilter,
    setCityFilter,
    setDueFilter,
    setPlannerWeekStart,
    setSelectedPlannerDate,
    setSort,
    setStatusFilter,
    setViewMode,
    sort,
    statusFilter,
    syncSelectionQuery,
    toggleSelection
  } = useLeadsViewStore();
  const [sortDirection, setSortDirection] = useState<SortDirection>(getDefaultLeadSortDirection(sort));

  useEffect(() => {
    let active = true;

    setError(null);
    void listLeads()
      .then((nextCollection) => {
        if (active) {
          setCollection(nextCollection);
        }
      })
      .catch((nextError) => {
        if (active) {
          setError(nextError instanceof Error ? nextError.message : 'Leads unavailable.');
        }
      });

    return () => {
      active = false;
    };
  }, [reloadToken]);

  useEffect(() => {
    return () => {
      clearSelection();
      setLeadBulkDraft(createInitialLeadBulkDraft());
    };
  }, [clearSelection]);

  useEffect(
    () =>
      subscribeToShellDomainInvalidations((detail) => {
        if (detail.domain === 'leads') {
          setReloadToken((value) => value + 1);
        }
      }),
    []
  );

  const summary = useMemo(() => (collection ? selectLeadsSummary(collection) : null), [collection]);
  const rows = useMemo(() => {
    if (!collection) {
      return [];
    }

    return selectLeadRows(collection, {
      locale,
      statusFilter,
      categoryFilter,
      cityFilter,
      dueFilter,
      sort,
      sortDirection,
      t
    });
  }, [categoryFilter, cityFilter, collection, dueFilter, locale, sort, sortDirection, statusFilter, t]);
  const leadColumns = useMemo<CollectionColumnConfig[]>(
    () => [
      { id: 'selection', label: '', className: 'av-collection-table__selection-cell', locked: true },
      { id: 'name', label: t('leads.table.name'), sortable: true, locked: true },
      { id: 'contact', label: t('leads.table.contact'), sortable: true },
      { id: 'category', label: t('leads.table.category'), sortable: true },
      { id: 'addedAt', label: t('leads.table.addedAt'), sortable: true },
      { id: 'status', label: t('leads.table.status'), sortable: true },
      { id: 'nextContact', label: t('leads.table.nextContact'), sortable: true },
      { id: 'website', label: t('leads.table.website'), sortable: true },
      { id: 'city', label: t('leads.table.city'), sortable: true },
      { id: 'actions', label: '', align: 'right', locked: true }
    ],
    [t]
  );
  const visibleLeadColumns = useMemo(
    () => resolveVisibleCollectionColumns(leadColumns, preferences.collectionColumnVisibility.leads),
    [leadColumns, preferences.collectionColumnVisibility.leads]
  );
  const categoryOptions = useMemo(() => (collection ? selectLeadCategoryOptions(collection, t) : []), [collection, t]);
  const cityOptions = useMemo(() => (collection ? selectLeadCityOptions(collection, t) : []), [collection, t]);
  const planningCityOptions = useMemo(() => cityOptions.filter((option) => option.value !== 'missing'), [cityOptions]);
  const planningCategoryOptions = useMemo(
    () => categoryOptions.filter((option) => option.value !== 'unassigned' && !option.description),
    [categoryOptions]
  );
  const plannerDays = useMemo(
    () =>
      collection
        ? selectLeadPlannerWeek(collection, {
            locale,
            weekStartDate: plannerWeekStart,
            selectedDate: selectedPlannerDate,
            statusFilter,
            categoryFilter,
            cityFilter,
            t
          })
        : [],
    [categoryFilter, cityFilter, collection, locale, plannerWeekStart, selectedPlannerDate, statusFilter, t]
  );
  const selectedPlannerDay = plannerDays.find((day) => day.isSelected) ?? plannerDays[0] ?? null;
  const bulkCategoryOptions = useMemo(
    () => categoryOptions.filter((option) => option.value !== 'all' && option.value !== 'unassigned' && !option.description),
    [categoryOptions]
  );
  const selectionItems = useMemo(() => (collection ? selectLeadSelectionItems(collection) : []), [collection]);
  const plannerVisibleIds = useMemo(
    () => plannerDays.flatMap((day) => day.groups.flatMap((group) => group.items.map((item) => item.id))),
    [plannerDays]
  );
  const visibleIds = useMemo(
    () => (preferences.leadsViewMode === 'planner' ? plannerVisibleIds : rows.map((row) => row.id)),
    [plannerVisibleIds, preferences.leadsViewMode, rows]
  );
  const visibleIdsKey = visibleIds.join('|');
  const queryKey = `${statusFilter}|${categoryFilter}|${cityFilter}|${dueFilter}|${sort}|${sortDirection}|${preferences.leadsViewMode}|${plannerWeekStart}|${selectedPlannerDate}`;
  const quickAddContext = useMemo(
    () =>
      createQuickAddContext({
        customers: collection?.customerSummaries ?? [],
        projects: collection?.projectSummaries ?? [],
        customerCategories: collection?.customerCategories ?? [],
        t
      }),
    [collection, t]
  );

  useEffect(() => {
    syncSelectionQuery(queryKey, visibleIds);
  }, [queryKey, syncSelectionQuery, visibleIds, visibleIdsKey]);

  async function handleDeleteSelected(leadIds: string[]): Promise<void> {
    if (leadIds.length === 0) {
      return;
    }

    setMutationError(null);

    try {
      const nextCollection = await deleteLeads({ ids: leadIds });
      setCollection(nextCollection);
      clearSelection();
      setLeadBulkDraft(createInitialLeadBulkDraft());
    } catch (nextError) {
      setMutationError(nextError instanceof Error ? nextError.message : 'Lead delete failed.');
    }
  }

  useEffect(
    () =>
      subscribeToShellDeleteRequests((detail) => {
        if (detail.domain !== 'leads' || detail.ids.length === 0) {
          return;
        }

        void handleDeleteSelected(detail.ids);
      }),
    [clearSelection]
  );

  if (error) {
    return (
      <div className="av-page">
        <EmptyCollectionState title={t('leads.title')} description={error} />
      </div>
    );
  }

  if (!collection || !summary) {
    return <div className="av-page" />;
  }

  const selectedItems = selectionSession.selectedIds
    .map((selectedId) => selectionItems.find((item) => item.id === selectedId) ?? null)
    .filter((item): item is NonNullable<typeof item> => item !== null);
  const leadBulkTagsMode =
    leadBulkDraft.tags.mode === 'set'
      ? leadBulkDraft.tags.value.length === 0
        ? 'clear'
        : 'set'
      : 'unchanged';
  const hasLeadBulkDraftChanges = Object.values(leadBulkDraft).some((patch) => patch.mode !== 'unchanged');
  const isLeadBulkDraftValid =
    (leadBulkDraft.customerCategoryId.mode !== 'set' || leadBulkDraft.customerCategoryId.value.length > 0) &&
    (leadBulkDraft.nextContactAt.mode !== 'set' || leadBulkDraft.nextContactAt.value.length > 0) &&
    (leadBulkDraft.city.mode !== 'set' || leadBulkDraft.city.value.trim().length > 0) &&
    (leadBulkDraft.source.mode !== 'set' || leadBulkDraft.source.value.trim().length > 0) &&
    (leadBulkDraft.lostReason.mode !== 'set' || leadBulkDraft.lostReason.value.trim().length > 0);
  const tableSortState: CollectionSortState = {
    columnId: leadSortToColumnId(sort),
    direction: sortDirection
  };

  function handleToggleSelection(leadId: string, event: MouseEvent<HTMLButtonElement>): void {
    event.preventDefault();
    event.stopPropagation();
    toggleSelection(leadId, visibleIds, queryKey, {
      additive: true,
      range: event.shiftKey
    });
  }

  function renderPhoneAction(phone: string | null): JSX.Element {
    if (!phone) {
      return <span>{t('common.notSet')}</span>;
    }

    return (
      <button className="av-inline-link av-inline-link-button" type="button" onClick={() => void openExternal(toTelUrl(phone))}>
        <Icon name="phone" />
        <span>{phone}</span>
      </button>
    );
  }

  function renderWebsiteAction(website: string | null): JSX.Element {
    if (!website) {
      return <span>{t('common.notSet')}</span>;
    }

    return (
      <button className="av-inline-link av-inline-link-button" type="button" onClick={() => void openExternal(website)}>
        <Icon name="website" />
        <span>{website}</span>
      </button>
    );
  }

  function updateLeadBulkDraft(patch: Partial<LeadBulkDraft>): void {
    setLeadBulkDraft((current) => ({
      ...current,
      ...patch
    }));
  }

  async function handleApplyBulkChanges(): Promise<void> {
    if (selectedItems.length === 0 || !hasLeadBulkDraftChanges || !isLeadBulkDraftValid) {
      return;
    }

    setMutationError(null);

    try {
      const nextCollection = await applyBulkLeadPatch({
        leadIds: selectionSession.selectedIds,
        ...leadBulkDraft
      });

      setCollection(nextCollection);
      clearSelection();
      setLeadBulkDraft(createInitialLeadBulkDraft());
    } catch (nextError) {
      setMutationError(nextError instanceof Error ? nextError.message : 'Lead update failed.');
    }
  }

  function handleTableSortChange(nextSortState: CollectionSortState): void {
    const nextSort = leadColumnIdToSort(nextSortState.columnId);

    if (!nextSort) {
      return;
    }

    setSort(nextSort);
    setSortDirection(nextSortState.direction);
  }

  function handleSortSelectChange(nextSort: LeadsSort): void {
    setSort(nextSort);
    setSortDirection(getDefaultLeadSortDirection(nextSort));
  }

  function handlePlannerWeekChange(nextWeekStart: string): void {
    const normalizedWeekStart = getWeekStartDate(nextWeekStart);
    setPlannerWeekStart(normalizedWeekStart);
    setSelectedPlannerDate(normalizedWeekStart);
  }

  async function handleAssignSelectedToPlannerDate(): Promise<void> {
    if (selectedItems.length === 0 || !selectedPlannerDay) {
      return;
    }

    setMutationError(null);
    setPlanningMessage(null);

    try {
      const nextCollection = await applyBulkLeadPatch({
        leadIds: selectionSession.selectedIds,
        status: { mode: 'unchanged' },
        customerCategoryId: { mode: 'unchanged' },
        nextContactAt: { mode: 'set', value: selectedPlannerDay.isoDate },
        city: { mode: 'unchanged' },
        source: { mode: 'unchanged' },
        lostReason: { mode: 'unchanged' },
        tags: { mode: 'unchanged' }
      });

      setCollection(nextCollection);
      clearSelection();
      setPlanningMessage(t('leads.planner.messages.manualAssigned', { count: selectedItems.length, date: selectedPlannerDay.label }));
    } catch (nextError) {
      setMutationError(nextError instanceof Error ? nextError.message : 'Lead planning failed.');
    }
  }

  async function handleClearSelectedPlannerDate(): Promise<void> {
    if (selectedItems.length === 0) {
      return;
    }

    setMutationError(null);
    setPlanningMessage(null);

    try {
      const nextCollection = await applyBulkLeadPatch({
        leadIds: selectionSession.selectedIds,
        status: { mode: 'unchanged' },
        customerCategoryId: { mode: 'unchanged' },
        nextContactAt: { mode: 'clear' },
        city: { mode: 'unchanged' },
        source: { mode: 'unchanged' },
        lostReason: { mode: 'unchanged' },
        tags: { mode: 'unchanged' }
      });

      setCollection(nextCollection);
      clearSelection();
      setPlanningMessage(t('leads.planner.messages.manualCleared', { count: selectedItems.length }));
    } catch (nextError) {
      setMutationError(nextError instanceof Error ? nextError.message : 'Lead planning failed.');
    }
  }

  function handlePlanningStatusToggle(status: LeadStatus): void {
    setPlanningDraft((current) => {
      const hasStatus = current.statusFilter.includes(status);
      return {
        ...current,
        statusFilter: hasStatus
          ? current.statusFilter.filter((entry) => entry !== status)
          : [...current.statusFilter, status]
      };
    });
  }

  async function handleSmartPlanContacts(): Promise<void> {
    const dayCount = Number.parseInt(planningDraft.dayCount, 10);
    const leadsPerDay = Number.parseInt(planningDraft.leadsPerDay, 10);

    if (!planningDraft.startDate || planningDraft.statusFilter.length === 0 || !Number.isFinite(dayCount) || !Number.isFinite(leadsPerDay)) {
      return;
    }

    setPlanningBusy(true);
    setMutationError(null);
    setPlanningMessage(null);

    try {
      const result = await planLeadContacts({
        city: planningDraft.cityFilter === 'all' || planningDraft.cityFilter === 'missing' ? null : planningDraft.cityFilter,
        customerCategoryId: planningDraft.categoryFilter === 'all' ? null : planningDraft.categoryFilter,
        statusFilter: planningDraft.statusFilter,
        startDate: toIsoDate(planningDraft.startDate),
        dayCount,
        leadsPerDay,
        skipAlreadyScheduled: true
      });

      setCollection(result.collection);
      setPlannerWeekStart(getWeekStartDate(planningDraft.startDate));
      setSelectedPlannerDate(planningDraft.startDate);
      setPlanningMessage(
        t('leads.planner.messages.smartFilled', {
          planned: result.plannedCount,
          skipped: result.skippedCount,
          days: result.daysFilled
        })
      );
    } catch (nextError) {
      setMutationError(nextError instanceof Error ? nextError.message : 'Lead planning failed.');
    } finally {
      setPlanningBusy(false);
    }
  }

  function handleVisibleColumnIdsChange(columnIds: string[]): void {
    void savePreferences({
      collectionColumnVisibility: {
        ...preferences.collectionColumnVisibility,
        leads: columnIds
      }
    });
  }

  function handleResetVisibleColumns(): void {
    const nextVisibility = { ...preferences.collectionColumnVisibility };
    delete nextVisibility.leads;
    void savePreferences({ collectionColumnVisibility: nextVisibility });
  }

  function handleNullablePatchModeChange(
    mode: PatchMode,
    fallbackValue: string,
    field: 'customerCategoryId' | 'nextContactAt' | 'city' | 'source' | 'lostReason'
  ): void {
    if (mode === 'set') {
      updateLeadBulkDraft({ [field]: { mode: 'set', value: fallbackValue } });
      return;
    }

    updateLeadBulkDraft({ [field]: { mode } });
  }

  function handleTagsModeChange(mode: PatchMode): void {
    if (mode === 'set' || mode === 'clear') {
      updateLeadBulkDraft({ tags: { mode: 'set', value: [] } });
      return;
    }

    updateLeadBulkDraft({ tags: { mode: 'unchanged' } });
  }

  function renderLeadCell(row: LeadRowViewModel, columnId: string): JSX.Element {
    const selected = selectionSession.selectedIds.includes(row.id);

    switch (columnId) {
      case 'selection':
        return (
          <td key={columnId} className="av-collection-table__cell av-collection-table__selection-cell">
            <CollectionSelectionToggle
              selected={selected}
              icon="leads"
              label={row.name}
              onClick={(event) => handleToggleSelection(row.id, event)}
            />
          </td>
        );
      case 'name':
        return (
          <td key={columnId} className="av-collection-table__cell">
            <div className="av-collection-table__identity-copy">
              <strong>
                <Link className="av-inline-link" to={`/leads/${row.id}`}>
                  <span>{row.name}</span>
                </Link>
              </strong>
              <div className="av-inline-metadata">
                {row.tags.map((tag) => (
                  <span key={tag} className="av-tag">
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          </td>
        );
      case 'contact':
        return (
          <td key={columnId} className="av-collection-table__cell">
            <div className="av-summary-stack">
              <strong>{row.contactLabel}</strong>
              {renderPhoneAction(row.phone)}
            </div>
          </td>
        );
      case 'category':
        return (
          <td key={columnId} className="av-collection-table__cell">
            <span className="av-category-chip">
              <Icon name={row.categoryIconName} />
              <span>{row.categoryLabel}</span>
              {row.categoryArchived ? <em>{t('common.archived')}</em> : null}
            </span>
          </td>
        );
      case 'addedAt':
        return (
          <td key={columnId} className="av-collection-table__cell">
            {row.createdAtLabel}
          </td>
        );
      case 'status':
        return (
          <td key={columnId} className="av-collection-table__cell">
            <LeadStatusControl
              disabled={updatingStatusLeadId === row.id}
              leadName={row.name}
              t={t}
              value={row.status}
              onChange={(status) => void handleChangeLeadStatus(row, status)}
            />
          </td>
        );
      case 'nextContact':
        return (
          <td key={columnId} className="av-collection-table__cell">
            <StatusBadge label={row.nextContactLabel} tone={row.nextContactTone} />
          </td>
        );
      case 'website':
        return (
          <td key={columnId} className="av-collection-table__cell">
            {renderWebsiteAction(row.website)}
          </td>
        );
      case 'city':
        return (
          <td key={columnId} className="av-collection-table__cell">
            {row.city ?? t('common.notSet')}
          </td>
        );
      case 'actions':
        return (
          <td key={columnId} className="av-collection-table__cell is-right">
            <OverflowMenuButton onClick={(event) => event.stopPropagation()} />
          </td>
        );
      default:
        return <td key={columnId} className="av-collection-table__cell" />;
    }
  }

  async function handleChangeLeadStatus(row: LeadRowViewModel, status: LeadStatus): Promise<void> {
    if (row.status === status || updatingStatusLeadId) {
      return;
    }

    setUpdatingStatusLeadId(row.id);
    setMutationError(null);

    try {
      const nextDetail = await updateLead({ id: row.id, status });
      setCollection((current) =>
        current
          ? {
              ...current,
              leads: current.leads.map((lead) => (lead.id === nextDetail.lead.id ? nextDetail.lead : lead)),
              customerSummaries: nextDetail.customerSummaries,
              projectSummaries: nextDetail.projectSummaries,
              customerCategories: nextDetail.customerCategories
            }
          : current
      );
    } catch (nextError) {
      setMutationError(nextError instanceof Error ? nextError.message : 'Lead status update failed.');
    } finally {
      setUpdatingStatusLeadId(null);
    }
  }

  function renderPlannerItem(item: LeadPlannerItemViewModel): JSX.Element {
    const selected = selectionSession.selectedIds.includes(item.id);

    return (
      <article className={['av-lead-planner__item', selected ? 'is-selected' : ''].filter(Boolean).join(' ')}>
        <CollectionSelectionToggle
          selected={selected}
          className="av-card-selection-toggle"
          icon="leads"
          label={item.name}
          onClick={(event) => handleToggleSelection(item.id, event)}
        />
        <div className="av-lead-planner__item-main">
          <div className="av-lead-planner__item-title">
            <Link className="av-inline-link" to={`/leads/${item.id}`}>
              <span>{item.name}</span>
            </Link>
            <StatusBadge label={item.statusLabel} tone={item.statusTone} />
          </div>
          <div className="av-inline-metadata">
            <span>{item.companyLabel}</span>
            <span>{item.contactLabel}</span>
            <span>{item.city ?? t('common.notSet')}</span>
            <span className="av-category-chip">
              <Icon name={item.categoryIconName} />
              <span>{item.categoryLabel}</span>
            </span>
            {item.contactedOnDay ? <StatusBadge label={t('leads.planner.badges.contactedToday')} tone="success" /> : null}
          </div>
          <div className="av-inline-metadata">
            {item.tags.map((tag) => (
              <span key={tag} className="av-tag">
                {tag}
              </span>
            ))}
            <span>{item.lastActivityLabel}</span>
          </div>
        </div>
        <div className="av-lead-planner__item-actions">
          {renderPhoneAction(item.phone)}
          {renderWebsiteAction(item.website)}
        </div>
      </article>
    );
  }

  function renderPlannerDayButton(day: LeadPlannerDayViewModel): JSX.Element {
    return (
      <button
        key={day.dateKey}
        aria-pressed={day.isSelected}
        className={[
          'av-lead-planner__day',
          day.isSelected ? 'is-selected' : '',
          day.isToday ? 'is-today' : '',
          day.isPast ? 'is-past' : ''
        ]
          .filter(Boolean)
          .join(' ')}
        type="button"
        onClick={() => setSelectedPlannerDate(day.dateKey)}
      >
        <span>{day.weekdayLabel}</span>
        <strong>{day.shortLabel}</strong>
        <small>{t('leads.planner.planned', { count: day.plannedCount })}</small>
      </button>
    );
  }

  function renderPlannerView(): JSX.Element {
    const fillDisabled =
      planningBusy ||
      !planningDraft.startDate ||
      planningDraft.statusFilter.length === 0 ||
      Number.parseInt(planningDraft.dayCount, 10) < 1 ||
      Number.parseInt(planningDraft.leadsPerDay, 10) < 1;
    const planningStatuses = leadStatusOrder.filter((status) => status !== 'converted' && status !== 'lost');

    return (
      <div className="av-lead-planner">
        <div className="av-lead-planner__toolbar">
          <div className="av-lead-planner__week-controls">
            <IconButton
              icon="arrowUp"
              label={t('leads.planner.previousWeek')}
              onClick={() => handlePlannerWeekChange(addDays(plannerWeekStart, -7))}
            />
            <label className="av-form-field av-lead-planner__week-input">
              <span>{t('leads.planner.weekStart')}</span>
              <TextField type="date" value={plannerWeekStart} onValueChange={handlePlannerWeekChange} />
            </label>
            <IconButton
              icon="arrowDown"
              label={t('leads.planner.nextWeek')}
              onClick={() => handlePlannerWeekChange(addDays(plannerWeekStart, 7))}
            />
            <button className="av-secondary-button" type="button" onClick={() => handlePlannerWeekChange(toDateKey(new Date()))}>
              {t('leads.planner.thisWeek')}
            </button>
          </div>
          <div className="av-lead-planner__manual-actions">
            <span>{t('leads.planner.selectedCount', { count: selectedItems.length })}</span>
            <button
              className="av-secondary-button"
              disabled={selectedItems.length === 0 || !selectedPlannerDay}
              type="button"
              onClick={() => void handleAssignSelectedToPlannerDate()}
            >
              {t('leads.planner.assignSelected')}
            </button>
            <button
              className="av-secondary-button"
              disabled={selectedItems.length === 0}
              type="button"
              onClick={() => void handleClearSelectedPlannerDate()}
            >
              {t('leads.planner.clearSelected')}
            </button>
          </div>
        </div>

        {planningMessage ? (
          <div className="av-lead-planner__message">
            <Icon name="check" />
            <span>{planningMessage}</span>
          </div>
        ) : null}

        <div className="av-lead-planner__week">{plannerDays.map((day) => renderPlannerDayButton(day))}</div>

        <div className="av-lead-planner__body">
          <section className="av-lead-planner__detail">
            <header className="av-lead-planner__section-header">
              <div>
                <h2>{selectedPlannerDay?.label ?? t('leads.planner.selectedDay')}</h2>
                <span>{t('leads.planner.selectedDay')}</span>
              </div>
              {selectedPlannerDay ? (
                <div className="av-lead-planner__day-stats">
                  <StatusBadge label={t('leads.planner.planned', { count: selectedPlannerDay.plannedCount })} tone="primary" />
                  <StatusBadge label={t('leads.planner.contacted', { count: selectedPlannerDay.contactedCount })} tone="success" />
                </div>
              ) : null}
            </header>

            {selectedPlannerDay && selectedPlannerDay.groups.length > 0 ? (
              <div className="av-lead-planner__groups">
                {selectedPlannerDay.groups.map((group) => (
                  <section key={group.id} className="av-lead-planner__group">
                    <header className="av-lead-planner__group-header">
                      <span className="av-category-chip">
                        <Icon name={group.categoryIconName} />
                        <span>{group.label}</span>
                      </span>
                      <strong>{group.items.length}</strong>
                    </header>
                    <div className="av-lead-planner__items">{group.items.map((item) => renderPlannerItem(item))}</div>
                  </section>
                ))}
              </div>
            ) : (
              <EmptyCollectionState
                title={t('leads.planner.emptyDayTitle')}
                description={t('leads.planner.emptyDayDescription')}
              />
            )}
          </section>

          <aside className="av-lead-planner__smart-fill">
            <header className="av-lead-planner__section-header">
              <div>
                <h2>{t('leads.planner.smartFill')}</h2>
                <span>{t('leads.planner.title')}</span>
              </div>
            </header>

            <div className="av-lead-planner__smart-grid">
              <label className="av-form-field">
                <span>{t('leads.planner.fields.city')}</span>
                <SearchableSelectField
                  clearLabel={t('common.clear')}
                  emptyLabel={t('common.noResults')}
                  options={planningCityOptions}
                  placeholder={t('leads.filters.allCities')}
                  searchPlaceholder={t('leads.filters.searchCity')}
                  value={planningDraft.cityFilter}
                  onValueChange={(value) =>
                    setPlanningDraft((current) => ({
                      ...current,
                      cityFilter: (value || 'all') as LeadsCityFilter
                    }))
                  }
                />
              </label>

              <label className="av-form-field">
                <span>{t('leads.planner.fields.customerType')}</span>
                <SearchableSelectField
                  clearLabel={t('common.clear')}
                  emptyLabel={t('common.noResults')}
                  options={planningCategoryOptions}
                  placeholder={t('leads.filters.allCategories')}
                  searchPlaceholder={t('leads.filters.searchCategory')}
                  value={planningDraft.categoryFilter}
                  onValueChange={(value) =>
                    setPlanningDraft((current) => ({
                      ...current,
                      categoryFilter: value || 'all'
                    }))
                  }
                />
              </label>

              <label className="av-form-field">
                <span>{t('leads.planner.fields.startDate')}</span>
                <TextField
                  type="date"
                  value={planningDraft.startDate}
                  onValueChange={(value) => setPlanningDraft((current) => ({ ...current, startDate: value }))}
                />
              </label>

              <div className="av-lead-planner__number-fields">
                <label className="av-form-field">
                  <span>{t('leads.planner.fields.dayCount')}</span>
                  <TextField
                    max={14}
                    min={1}
                    type="number"
                    value={planningDraft.dayCount}
                    onValueChange={(value) => setPlanningDraft((current) => ({ ...current, dayCount: value }))}
                  />
                </label>
                <label className="av-form-field">
                  <span>{t('leads.planner.fields.leadsPerDay')}</span>
                  <TextField
                    max={200}
                    min={1}
                    type="number"
                    value={planningDraft.leadsPerDay}
                    onValueChange={(value) => setPlanningDraft((current) => ({ ...current, leadsPerDay: value }))}
                  />
                </label>
              </div>

              <fieldset className="av-lead-planner__status-grid">
                <legend>{t('leads.planner.fields.statuses')}</legend>
                {planningStatuses.map((status) => (
                  <label key={status} className="av-checkbox-field">
                    <input
                      checked={planningDraft.statusFilter.includes(status)}
                      type="checkbox"
                      onChange={() => handlePlanningStatusToggle(status)}
                    />
                    <span>{t(`enums.leadStatus.${status}`)}</span>
                  </label>
                ))}
              </fieldset>

              <button className="av-primary-button" disabled={fillDisabled} type="button" onClick={() => void handleSmartPlanContacts()}>
                {planningBusy ? t('leads.planner.actions.smartFilling') : t('leads.planner.actions.smartFill')}
              </button>
            </div>
          </aside>
        </div>
      </div>
    );
  }

  return (
    <div className="av-page">
      <SectionHeader
        title={t('leads.title')}
        subtitle={`${t('leads.summary.active', { count: summary.activeCount })} - ${t('leads.summary.due', {
          count: summary.dueCount
        })} - ${t('leads.summary.converted', { count: summary.convertedCount })}`}
      />

      <InlineQuickAddPanel domain="leads" context={quickAddContext} onCreated={async () => setCollection(await listLeads())} />

      {mutationError ? <div className="av-inline-error">{mutationError}</div> : null}

      <CollectionToolbar
        primary={
          <ToolbarGroup>
            <SelectField
              aria-label={t('leads.filters.status')}
              options={leadStatusFilters.map((filter) => ({
                value: filter,
                label: filter === 'all' ? t('leads.filters.all') : t(`enums.leadStatus.${filter}`)
              }))}
              value={statusFilter}
              onChange={(event) => setStatusFilter(event.target.value as 'all' | LeadStatus)}
            />
          </ToolbarGroup>
        }
        secondary={
          <ToolbarGroup>
            <SearchableSelectField
              clearLabel={t('common.clear')}
              emptyLabel={t('common.noResults')}
              options={categoryOptions}
              placeholder={t('leads.filters.categoryPlaceholder')}
              searchPlaceholder={t('leads.filters.searchCategory')}
              value={categoryFilter}
              onValueChange={(value) => setCategoryFilter(value || 'all')}
            />
            <SearchableSelectField
              clearLabel={t('common.clear')}
              emptyLabel={t('common.noResults')}
              options={cityOptions}
              placeholder={t('leads.filters.allCities')}
              searchPlaceholder={t('leads.filters.searchCity')}
              value={cityFilter}
              onValueChange={(value) => setCityFilter((value || 'all') as LeadsCityFilter)}
            />
            {preferences.leadsViewMode === 'planner' ? null : (
              <>
                <SelectField
                  options={[
                    { value: 'all', label: t('leads.filters.allFollowUps') },
                    { value: 'due', label: t('leads.filters.due') },
                    { value: 'scheduled', label: t('leads.filters.scheduled') },
                    { value: 'none', label: t('leads.filters.noFollowUp') }
                  ]}
                  value={dueFilter}
                  onChange={(event) => setDueFilter(event.target.value as LeadsDueFilter)}
                />
                <SelectField
                  options={[
                    { value: 'nextContact', label: t('leads.sort.nextContact') },
                    { value: 'recent', label: t('leads.sort.addedAt') },
                    { value: 'name', label: t('leads.sort.name') },
                    { value: 'contact', label: t('leads.sort.contact') },
                    { value: 'category', label: t('leads.sort.category') },
                    { value: 'status', label: t('leads.sort.status') },
                    { value: 'website', label: t('leads.sort.website') },
                    { value: 'city', label: t('leads.sort.city') }
                  ]}
                  value={sort}
                  onChange={(event) => handleSortSelectChange(event.target.value as LeadsSort)}
                />
              </>
            )}
            <CollectionViewToggle<LeadsViewMode>
              options={[
                { value: 'list', label: t('common.view.list'), icon: 'list' },
                { value: 'grid', label: t('common.view.grid'), icon: 'grid' },
                { value: 'planner', label: t('leads.view.planner'), icon: 'calendar' }
              ]}
              value={preferences.leadsViewMode}
              onChange={(value) => void setViewMode(value)}
            />
            <IconButton
              icon="settings"
              label={t('common.columns.settingsButton')}
              onClick={() => setColumnSettingsOpen(true)}
            />
          </ToolbarGroup>
        }
      />

      <SplitWorkspaceLayout
        railMode="fixed"
        main={
          preferences.leadsViewMode === 'planner' ? (
            renderPlannerView()
          ) : rows.length === 0 ? (
            <EmptyCollectionState title={t('leads.title')} description={t('common.noResults')} />
          ) : preferences.leadsViewMode === 'grid' ? (
            <CardGridSurface mode="grid">
              {rows.map((row) => {
                const selected = selectionSession.selectedIds.includes(row.id);

                return (
                  <PanelCard
                    key={row.id}
                    actions={<OverflowMenuButton onClick={(event) => event.stopPropagation()} />}
                    className={['av-collection-card', selected ? 'is-selected' : ''].filter(Boolean).join(' ')}
                  >
                    <div className="av-project-card__header">
                      <div className="av-project-card__identity">
                        <div className="av-project-card__icon-wrap">
                          <CollectionSelectionToggle
                            selected={selected}
                            className="av-card-selection-toggle"
                            icon="leads"
                            label={row.name}
                            onClick={(event) => handleToggleSelection(row.id, event)}
                          />
                        </div>
                        <div>
                          <h2>
                            <Link className="av-inline-link" to={`/leads/${row.id}`}>
                              <span>{row.name}</span>
                            </Link>
                          </h2>
                          <div className="av-project-card__meta av-lead-card__status-row">
                            <LeadStatusControl
                              disabled={updatingStatusLeadId === row.id}
                              leadName={row.name}
                              t={t}
                              value={row.status}
                              onChange={(status) => void handleChangeLeadStatus(row, status)}
                            />
                            <span className="av-category-chip">
                              <Icon name={row.categoryIconName} />
                              <span>{row.categoryLabel}</span>
                              {row.categoryArchived ? <em>{t('common.archived')}</em> : null}
                            </span>
                            <StatusBadge label={row.nextContactLabel} tone={row.nextContactTone} />
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="av-summary-stack">
                      <span>{row.companyLabel}</span>
                      <span>
                        {t('leads.editor.fields.city')}: {row.city ?? t('common.notSet')}
                      </span>
                      {renderPhoneAction(row.phone)}
                      {renderWebsiteAction(row.website)}
                      <span>
                        {t('leads.card.addedAt')}: {row.createdAtLabel}
                      </span>
                    </div>
                    <div className="av-project-card__footer">
                      <div>
                        <strong>{t('leads.card.lastActivity')}</strong>
                        <span> - {row.lastActivityLabel}</span>
                      </div>
                    </div>
                  </PanelCard>
                );
              })}
            </CardGridSurface>
          ) : (
            <DataTableSurface>
              <CollectionTable
                ariaLabel={t('leads.title')}
                columns={visibleLeadColumns}
                sortState={tableSortState}
                onSortChange={handleTableSortChange}
              >
                {rows.map((row) => {
                  const selected = selectionSession.selectedIds.includes(row.id);

                  return (
                    <tr key={row.id} className={['av-collection-table__row', selected ? 'is-selected' : ''].filter(Boolean).join(' ')}>
                      {visibleLeadColumns.map((column) => renderLeadCell(row, column.id))}
                    </tr>
                  );
                })}
              </CollectionTable>
            </DataTableSurface>
          )
        }
        rail={
          selectedItems.length > 0 ? (
            <BulkEditorRail
              title={t('leads.selection.title', { count: selectedItems.length })}
              hiddenSelectedCount={selectionSession.hiddenSelectedCount}
              items={selectedItems.map((item) => ({
                id: item.id,
                icon: 'leads',
                label: item.label,
                subtitle: item.subtitle
              }))}
              onClose={() => {
                clearSelection();
                setLeadBulkDraft(createInitialLeadBulkDraft());
              }}
              onRemoveItem={(itemId) => removeSelectedId(itemId, visibleIds)}
              destructiveAction={{
                label: t('shell.delete'),
                onClick: () => void handleDeleteSelected(selectionSession.selectedIds)
              }}
              primaryAction={{
                label: t('common.applyChanges'),
                disabled: !hasLeadBulkDraftChanges || !isLeadBulkDraftValid,
                onClick: () => void handleApplyBulkChanges()
              }}
              secondaryAction={{
                label: t('common.cancel'),
                onClick: () => {
                  clearSelection();
                  setLeadBulkDraft(createInitialLeadBulkDraft());
                }
              }}
            >
              <div className="av-bulk-editor-form">
                <label className="av-form-field">
                  <span>{t('leads.editor.fields.status')}</span>
                  <SelectField
                    options={[
                      { value: 'unchanged', label: t('common.leaveUnchanged') },
                      ...leadStatusOrder.map((status) => ({
                        value: status,
                        label: t(`enums.leadStatus.${status}`)
                      }))
                    ]}
                    value={leadBulkDraft.status.mode === 'set' ? leadBulkDraft.status.value : 'unchanged'}
                    onChange={(event) =>
                      updateLeadBulkDraft({
                        status:
                          event.target.value === 'unchanged'
                            ? { mode: 'unchanged' }
                            : { mode: 'set', value: event.target.value as LeadStatus }
                      })
                    }
                  />
                </label>

                <label className="av-form-field">
                  <span>{t('leads.editor.fields.customerCategory')}</span>
                  <SelectField
                    options={[
                      { value: 'unchanged', label: t('common.leaveUnchanged') },
                      { value: 'clear', label: t('common.clearValue') },
                      { value: 'set', label: t('common.setValue') }
                    ]}
                    value={leadBulkDraft.customerCategoryId.mode}
                    onChange={(event) =>
                      handleNullablePatchModeChange(
                        event.target.value as PatchMode,
                        bulkCategoryOptions[0]?.value ?? '',
                        'customerCategoryId'
                      )
                    }
                  />
                  {leadBulkDraft.customerCategoryId.mode === 'set' ? (
                    <SearchableSelectField
                      clearLabel={t('common.clear')}
                      emptyLabel={t('common.noResults')}
                      options={bulkCategoryOptions}
                      placeholder={t('leads.filters.categoryPlaceholder')}
                      searchPlaceholder={t('leads.filters.searchCategory')}
                      value={leadBulkDraft.customerCategoryId.value}
                      onValueChange={(value) => updateLeadBulkDraft({ customerCategoryId: { mode: 'set', value } })}
                    />
                  ) : null}
                </label>

                <label className="av-form-field">
                  <span>{t('leads.editor.fields.nextContactAt')}</span>
                  <SelectField
                    options={[
                      { value: 'unchanged', label: t('common.leaveUnchanged') },
                      { value: 'clear', label: t('common.clearValue') },
                      { value: 'set', label: t('common.setValue') }
                    ]}
                    value={leadBulkDraft.nextContactAt.mode}
                    onChange={(event) =>
                      handleNullablePatchModeChange(event.target.value as PatchMode, new Date().toISOString(), 'nextContactAt')
                    }
                  />
                  {leadBulkDraft.nextContactAt.mode === 'set' ? (
                    <TextField
                      type="date"
                      value={formatDateInputValue(leadBulkDraft.nextContactAt.value)}
                      onValueChange={(value) =>
                        updateLeadBulkDraft({
                          nextContactAt: { mode: 'set', value: value ? toIsoDate(value) : '' }
                        })
                      }
                    />
                  ) : null}
                </label>

                <label className="av-form-field">
                  <span>{t('leads.editor.fields.city')}</span>
                  <SelectField
                    options={[
                      { value: 'unchanged', label: t('common.leaveUnchanged') },
                      { value: 'clear', label: t('common.clearValue') },
                      { value: 'set', label: t('common.setValue') }
                    ]}
                    value={leadBulkDraft.city.mode}
                    onChange={(event) => handleNullablePatchModeChange(event.target.value as PatchMode, '', 'city')}
                  />
                  {leadBulkDraft.city.mode === 'set' ? (
                    <TextField
                      value={leadBulkDraft.city.value}
                      onValueChange={(value) => updateLeadBulkDraft({ city: { mode: 'set', value } })}
                    />
                  ) : null}
                </label>

                <label className="av-form-field">
                  <span>{t('leads.editor.fields.source')}</span>
                  <SelectField
                    options={[
                      { value: 'unchanged', label: t('common.leaveUnchanged') },
                      { value: 'clear', label: t('common.clearValue') },
                      { value: 'set', label: t('common.setValue') }
                    ]}
                    value={leadBulkDraft.source.mode}
                    onChange={(event) => handleNullablePatchModeChange(event.target.value as PatchMode, '', 'source')}
                  />
                  {leadBulkDraft.source.mode === 'set' ? (
                    <TextField
                      value={leadBulkDraft.source.value}
                      onValueChange={(value) => updateLeadBulkDraft({ source: { mode: 'set', value } })}
                    />
                  ) : null}
                </label>

                <label className="av-form-field">
                  <span>{t('leads.editor.fields.lostReason')}</span>
                  <SelectField
                    options={[
                      { value: 'unchanged', label: t('common.leaveUnchanged') },
                      { value: 'clear', label: t('common.clearValue') },
                      { value: 'set', label: t('common.setValue') }
                    ]}
                    value={leadBulkDraft.lostReason.mode}
                    onChange={(event) => handleNullablePatchModeChange(event.target.value as PatchMode, '', 'lostReason')}
                  />
                  {leadBulkDraft.lostReason.mode === 'set' ? (
                    <TextField
                      value={leadBulkDraft.lostReason.value}
                      onValueChange={(value) => updateLeadBulkDraft({ lostReason: { mode: 'set', value } })}
                    />
                  ) : null}
                </label>

                <label className="av-form-field">
                  <span>{t('leads.editor.fields.tags')}</span>
                  <SelectField
                    options={[
                      { value: 'unchanged', label: t('common.leaveUnchanged') },
                      { value: 'clear', label: t('common.clearValue') },
                      { value: 'set', label: t('common.setValue') }
                    ]}
                    value={leadBulkTagsMode}
                    onChange={(event) => handleTagsModeChange(event.target.value as PatchMode)}
                  />
                  {leadBulkDraft.tags.mode === 'set' ? (
                    <TextField
                      value={leadBulkDraft.tags.value.join(', ')}
                      onValueChange={(value) => updateLeadBulkDraft({ tags: { mode: 'set', value: toTags(value) } })}
                    />
                  ) : null}
                </label>
              </div>
            </BulkEditorRail>
          ) : null
        }
      />

      <CollectionColumnSettingsModal
        columns={leadColumns}
        domain="leads"
        extraTab={{
          id: 'importExport',
          label: t('leads.importExport.tabs.importExport'),
          content: <LeadImportExportPanel onCollectionChange={setCollection} />
        }}
        isOpen={columnSettingsOpen}
        strings={{
          title: t('common.columns.settingsTitle'),
          description: t('common.columns.settingsDescription'),
          columnsTab: t('leads.importExport.tabs.columns'),
          reset: t('common.columns.settingsReset'),
          close: t('common.close'),
          locked: t('common.columns.settingsLocked')
        }}
        visibleColumnIds={preferences.collectionColumnVisibility.leads}
        onClose={() => setColumnSettingsOpen(false)}
        onReset={handleResetVisibleColumns}
        onVisibleColumnIdsChange={handleVisibleColumnIdsChange}
      />
    </div>
  );
}
